import {
  ClockIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../constants/urls';
interface Organization {
  id: string;
  name: string;
  slug: string;
  plan_type: string;
  billing_type: string;
  subscription_status: string | null;
  current_period_end: string | null;
  is_trial_active: boolean;
  trial_end_date: string | null;
  genai_access_override: boolean | null;
  max_total_users_override: number | null;
  max_editors_override: number | null;
  member_count?: number;
  editor_count?: number;
  created_at: string;
  updated_at: string;
  // Stripe fields
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

interface EditOrganizationModal {
  organization: Organization;
  onClose: () => void;
  onUpdate: (updatedOrg: Organization) => void;
}

const EditOrganizationModal: React.FC<EditOrganizationModal> = ({
  organization,
  onClose,
  onUpdate,
}) => {
  const { token } = useAuth();
  // Store the full organization data including trial info
  const [currentOrg, setCurrentOrg] = useState<Organization>(organization);
  const [formData, setFormData] = useState({
    name: organization.name,
    plan_type: organization.plan_type,
    billing_type: organization.billing_type?.toLowerCase() || '',
    subscription_status: organization.subscription_status || '',
    genai_access_override:
      organization.genai_access_override?.toString() || 'null',
    max_total_users_override:
      organization.max_total_users_override?.toString() || '',
    max_editors_override: organization.max_editors_override?.toString() || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [stripeStatus, setStripeStatus] = useState<{
    checking: boolean;
    customerExists?: boolean;
    subscriptionExists?: boolean;
    error?: string;
  }>({ checking: false });

  // Sync currentOrg when organization prop changes
  useEffect(() => {
    setCurrentOrg(organization);
  }, [organization]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const updateData: any = {
        name: formData.name,
        plan_type: formData.plan_type,
        billing_type: formData.billing_type,
        subscription_status: formData.subscription_status || null,
        // Include current trial information to preserve it
        trial_end_date: currentOrg.trial_end_date,
        is_trial_active: currentOrg.is_trial_active,
      };

      // Handle overrides
      if (formData.genai_access_override !== 'null') {
        updateData.genai_access_override =
          formData.genai_access_override === 'true';
      }
      if (formData.max_total_users_override) {
        updateData.max_total_users_override = parseInt(
          formData.max_total_users_override,
        );
      }
      if (formData.max_editors_override) {
        updateData.max_editors_override = parseInt(
          formData.max_editors_override,
        );
      }

      const response = await fetch(
        `${API_URL}/admin/organizations/${organization.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update organization');
      }

      const updatedOrg = await response.json();
      // Merge the response with current state to ensure all fields are preserved
      const mergedOrg = { ...currentOrg, ...updatedOrg };
      setCurrentOrg(mergedOrg); // Update local state
      onUpdate(mergedOrg);
      setSuccessMessage('Organization updated successfully');
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      // console.error removed
      setError(
        err instanceof Error ? err.message : 'Failed to update organization',
      );
    } finally {
      setSaving(false);
    }
  };

  const startTrial = async (days: number) => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/admin/organizations/${currentOrg.id}/start-trial?days=${days}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start trial');
      }

      const result = await response.json();
      // Update the organization with the new trial status
      const updatedOrg = {
        ...currentOrg,
        is_trial_active: result.is_trial_active,
        trial_end_date: result.trial_end_date,
      };
      setCurrentOrg(updatedOrg); // Update local state
      onUpdate(updatedOrg); // Update parent state
      setSuccessMessage(`Started ${days}-day trial`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      // console.error removed
      setError(err instanceof Error ? err.message : 'Failed to start trial');
    } finally {
      setSaving(false);
    }
  };

  const extendTrial = async (days: number) => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/admin/organizations/${currentOrg.id}/extend-trial?additional_days=${days}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to extend trial');
      }

      const result = await response.json();
      // Update the organization with the new trial status
      const updatedOrg = {
        ...currentOrg,
        is_trial_active: result.is_trial_active,
        trial_end_date: result.trial_end_date,
      };
      setCurrentOrg(updatedOrg); // Update local state
      onUpdate(updatedOrg); // Update parent state
      setSuccessMessage(`Extended trial by ${days} days`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      // console.error removed
      setError(err instanceof Error ? err.message : 'Failed to extend trial');
    } finally {
      setSaving(false);
    }
  };

  const endTrial = async () => {
    if (
      !window.confirm('Are you sure you want to end the trial immediately?')
    ) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/admin/organizations/${currentOrg.id}/end-trial`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to end trial');
      }

      const result = await response.json();
      // Update the organization with the new trial status
      const updatedOrg = {
        ...currentOrg,
        is_trial_active: result.is_trial_active,
        trial_end_date:
          result.trial_status?.trial_end_date || result.trial_end_date, // Keep the end date from API
      };
      setCurrentOrg(updatedOrg); // Update local state
      onUpdate(updatedOrg); // Update parent state
      setSuccessMessage('Trial ended');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      // console.error removed
      setError(err instanceof Error ? err.message : 'Failed to end trial');
    } finally {
      setSaving(false);
    }
  };

  const clearTrial = async () => {
    if (
      !window.confirm(
        'Are you sure you want to clear all trial history? This will make the organization eligible for a new trial as if they never had one.',
      )
    ) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/admin/organizations/${currentOrg.id}/clear-trial`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to clear trial');
      }

      const result = await response.json();
      // Update the organization with cleared trial data
      const updatedOrg = {
        ...currentOrg,
        is_trial_active: false,
        trial_end_date: null,
      };
      setCurrentOrg(updatedOrg); // Update local state
      onUpdate(updatedOrg); // Update parent state
      setSuccessMessage(
        'Trial history cleared - organization can now start a new trial',
      );
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      // console.error removed
      setError(err instanceof Error ? err.message : 'Failed to clear trial');
    } finally {
      setSaving(false);
    }
  };

  const verifyStripeCustomer = async () => {
    setStripeStatus({ checking: true });
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/admin/organizations/${organization.id}/verify-stripe-customer`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to verify Stripe customer');
      }

      const result = await response.json();
      setStripeStatus({
        checking: false,
        customerExists: result.customer_exists,
        error: result.error,
      });

      if (result.action === 'reset') {
        const updatedOrg = {
          ...currentOrg,
          ...result.organization_state,
        };
        setCurrentOrg(updatedOrg);
        onUpdate(updatedOrg);
        setError(null);
        // Show in UI instead of alert
      }
    } catch (err) {
      setStripeStatus({
        checking: false,
        error: err instanceof Error ? err.message : 'Failed to verify',
      });
    }
  };

  const resetStripeBilling = async () => {
    if (
      !window.confirm(
        `Are you sure you want to reset Stripe billing for ${currentOrg.name}? This will clear all Stripe data and set them to free plan.`,
      )
    ) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/admin/organizations/${organization.id}/reset-stripe-billing`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to reset Stripe billing');
      }

      const result = await response.json();
      // Update the organization with the new state from the API
      const updatedOrg = {
        ...currentOrg,
        ...result.new_state,
      };
      setCurrentOrg(updatedOrg);
      onUpdate(updatedOrg);
      // Show success in UI instead of alert
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to reset Stripe billing',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500/75" onClick={onClose} />
        <div className="relative bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Edit Organization: {organization.name}
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Organization Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Plan Type
              </label>
              <select
                value={formData.plan_type}
                onChange={(e) =>
                  setFormData({ ...formData, plan_type: e.target.value })
                }
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="free">Free</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Billing Type
              </label>
              <select
                value={formData.billing_type}
                onChange={(e) =>
                  setFormData({ ...formData, billing_type: e.target.value })
                }
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="stripe">Stripe</option>
                <option value="manual">Manual</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Subscription Status
              </label>
              <select
                value={formData.subscription_status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    subscription_status: e.target.value,
                  })
                }
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">None</option>
                <option value="active">Active</option>
                <option value="past_due">Past Due</option>
                <option value="canceled">Canceled</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                GenAI Access Override
              </label>
              <select
                value={formData.genai_access_override}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    genai_access_override: e.target.value,
                  })
                }
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="null">Use Plan Default</option>
                <option value="true">Force Enable</option>
                <option value="false">Force Disable</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Max Users Override
                </label>
                <input
                  type="number"
                  value={formData.max_total_users_override}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_total_users_override: e.target.value,
                    })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Leave empty for plan default"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Max Editors Override
                </label>
                <input
                  type="number"
                  value={formData.max_editors_override}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_editors_override: e.target.value,
                    })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Leave empty for plan default"
                />
              </div>
            </div>

            {/* Stripe Sync Section - Shows when billing type is 'stripe' */}
            {formData.billing_type === 'stripe' && (
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Stripe Synchronization
                </h3>

                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Customer ID:
                      </p>
                      <p className="text-sm text-gray-600 font-mono">
                        {currentOrg.stripe_customer_id || 'Not set'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Subscription ID:
                      </p>
                      <p className="text-sm text-gray-600 font-mono">
                        {currentOrg.stripe_subscription_id || 'Not set'}
                      </p>
                    </div>
                  </div>

                  {stripeStatus.customerExists !== undefined && (
                    <div
                      className={`p-3 rounded flex items-center gap-2 ${
                        stripeStatus.customerExists
                          ? 'bg-green-50 text-green-800'
                          : 'bg-red-50 text-red-800'
                      }`}
                    >
                      {stripeStatus.customerExists ? (
                        <>
                          <CheckCircleIcon className="w-5 h-5" />
                          <span>Customer exists in Stripe</span>
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="w-5 h-5" />
                          <span>Customer not found in Stripe</span>
                        </>
                      )}
                    </div>
                  )}

                  {stripeStatus.error && (
                    <div className="p-3 bg-red-50 text-red-800 rounded">
                      {stripeStatus.error}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={verifyStripeCustomer}
                      disabled={stripeStatus.checking || saving}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                    >
                      {stripeStatus.checking ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 animate-spin" />
                          Checking...
                        </>
                      ) : (
                        <>
                          <ArrowPathIcon className="w-4 h-4" />
                          Verify & Fix
                        </>
                      )}
                    </button>

                    {currentOrg.stripe_customer_id && (
                      <button
                        type="button"
                        onClick={resetStripeBilling}
                        disabled={saving}
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                      >
                        Reset Stripe Billing
                      </button>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    <strong>Verify & Fix:</strong> Checks if the customer exists
                    in Stripe. If not, automatically resets to free plan.
                    <br />
                    <strong>Reset:</strong> Clears all Stripe data and sets
                    organization to free plan.
                  </p>
                </div>
              </div>
            )}

            {/* Trial Management Section */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Trial Management
              </h3>

              {/* Current Trial Status */}
              <div className="bg-gray-50 p-3 rounded mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Trial Active:
                    </p>
                    <p className="text-sm text-gray-600">
                      {currentOrg.is_trial_active ? (
                        <span className="text-green-600 font-medium">Yes</span>
                      ) : (
                        <span className="text-gray-500">No</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Trial End Date:
                    </p>
                    <p className="text-sm text-gray-600">
                      {currentOrg.trial_end_date ? (
                        new Date(currentOrg.trial_end_date).toLocaleDateString(
                          'en-GB',
                          {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          },
                        )
                      ) : (
                        <span className="text-gray-500">Never had trial</span>
                      )}
                    </p>
                  </div>
                </div>
                {currentOrg.trial_end_date && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500">
                      {new Date(currentOrg.trial_end_date) > new Date() ? (
                        <>
                          Trial expires in{' '}
                          {Math.ceil(
                            (new Date(currentOrg.trial_end_date).getTime() -
                              new Date().getTime()) /
                              (1000 * 60 * 60 * 24),
                          )}{' '}
                          days
                        </>
                      ) : (
                        <>
                          Trial expired{' '}
                          {Math.abs(
                            Math.ceil(
                              (new Date(currentOrg.trial_end_date).getTime() -
                                new Date().getTime()) /
                                (1000 * 60 * 60 * 24),
                            ),
                          )}{' '}
                          days ago
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Trial Action Buttons */}
              <div className="flex flex-wrap gap-2">
                {!currentOrg.is_trial_active ? (
                  <>
                    <button
                      type="button"
                      onClick={() => startTrial(7)}
                      disabled={saving}
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                    >
                      Start 7-Day Trial
                    </button>
                    <button
                      type="button"
                      onClick={() => startTrial(14)}
                      disabled={saving}
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                    >
                      Start 14-Day Trial
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => extendTrial(7)}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                    >
                      Extend Trial 7 Days
                    </button>
                    <button
                      type="button"
                      onClick={() => extendTrial(14)}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                    >
                      Extend Trial 14 Days
                    </button>
                    <button
                      type="button"
                      onClick={endTrial}
                      disabled={saving}
                      className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                    >
                      End Trial
                    </button>
                  </>
                )}

                {/* Clear Trial Button - Always Show */}
                {currentOrg.trial_end_date && (
                  <button
                    type="button"
                    onClick={clearTrial}
                    disabled={saving}
                    className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
                  >
                    Clear Trial History
                  </button>
                )}
              </div>

              <p className="text-xs text-gray-500 mt-2">
                <strong>Start/Extend Trial:</strong> Grants or extends trial
                access.
                <br />
                <strong>End Trial:</strong> Immediately ends the active trial.
                <br />
                <strong>Clear Trial History:</strong> Removes all trial data,
                making the organization eligible for a new trial (as if they
                never had one).
              </p>
            </div>

            {/* Form Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t mt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const SuperAdminOrganizations: React.FC = () => {
  const { user, token } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!user || user.role !== 'super_admin') {
        setError('Access denied: Super admin role required');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/admin/organizations`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch organizations: ${response.statusText}`,
          );
        }

        const data = await response.json();
        console.log('[DEBUG] Organizations received:', data);
        // Log the first organization's trial data specifically
        if (data.length > 0) {
          console.log('[DEBUG] First org trial data:', {
            name: data[0].name,
            is_trial_active: data[0].is_trial_active,
            trial_end_date: data[0].trial_end_date,
            // Log all fields to see what we're getting
            all_fields: Object.keys(data[0]),
          });
        }
        setOrganizations(data);
      } catch (err) {
        // console.error removed
        setError(
          err instanceof Error ? err.message : 'Failed to fetch organizations',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [user, token, API_URL]);

  const handleOrganizationUpdate = (updatedOrg: Organization) => {
    setOrganizations((orgs) =>
      orgs.map((org) => (org.id === updatedOrg.id ? updatedOrg : org)),
    );
  };

  if (!user || user.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Access Denied
          </h1>
          <p className="text-gray-600">
            This area requires super admin privileges.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">
          Organization Management
        </h1>
        <p className="text-gray-600 mt-2">
          Manage all organizations, plans, and billing settings
        </p>
      </div>

      {/* Organizations Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stripe
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trial
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {organizations.map((org) => {
                // Debug log for each org
                console.log(`[DEBUG] Rendering org ${org.name}:`, {
                  is_trial_active: org.is_trial_active,
                  trial_end_date: org.trial_end_date,
                  type_of_is_trial_active: typeof org.is_trial_active,
                  all_keys: Object.keys(org),
                });
                return (
                  <tr key={org.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {org.name}
                        </div>
                        <div className="text-sm text-gray-500">{org.slug}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          org.plan_type === 'free'
                            ? 'bg-gray-100 text-gray-800'
                            : org.plan_type === 'starter'
                              ? 'bg-blue-100 text-blue-800'
                              : org.plan_type === 'professional'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {org.plan_type.charAt(0).toUpperCase() +
                          org.plan_type.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <UserGroupIcon className="h-4 w-4 mr-1" />
                        {org.member_count || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {org.subscription_status ? (
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            org.subscription_status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : org.subscription_status === 'past_due'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {org.subscription_status}
                        </span>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {org.billing_type?.toLowerCase() === 'stripe' &&
                      org.stripe_customer_id ? (
                        <div className="flex items-center">
                          <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mr-1" />
                          <span className="text-xs text-gray-600">Check</span>
                        </div>
                      ) : org.billing_type?.toLowerCase() === 'stripe' ? (
                        <span className="text-xs text-gray-400">
                          No customer
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {org.billing_type}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {org.is_trial_active ? (
                        <div>
                          <div className="flex items-center text-sm text-green-600">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            Active
                          </div>
                          {org.trial_end_date && (
                            <div className="text-xs text-gray-500 mt-1">
                              Until{' '}
                              {new Date(org.trial_end_date).toLocaleDateString(
                                'en-GB',
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Inactive</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setEditingOrg(org)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {editingOrg && (
        <EditOrganizationModal
          organization={editingOrg}
          onClose={() => setEditingOrg(null)}
          onUpdate={handleOrganizationUpdate}
        />
      )}
    </div>
  );
};

export default SuperAdminOrganizations;
